import { inject, Injectable } from '@angular/core';
import { CurrencyService } from '@rd/core/services/currency/currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { PointsTransactionHelper } from '@rd/helpers/points-transaction.helper';
import { StringHelper } from '@rd/helpers/string.helper';
import {
  CardSpendMetadata,
  Enrollment,
  PointsTransaction,
  PointsTransactionCategory,
  RedeemCashMetadata,
  TravelCarMetadata,
  TravelFlightMetadata,
  TravelHotelMetadata,
} from '@rd/types';
import { VMA_HOME_LOAN_ENABLED } from '../core/tokens/features.tokens';
import { VmaEnrollmentHelper } from './vma-enrollment.helper';

@Injectable({
  providedIn: 'root',
})
export class VmaPointsTransactionHelper extends PointsTransactionHelper {
  constructor(
    protected numberService: NumberService,
    protected currencyService: CurrencyService,
    protected stringHelper: StringHelper,
    protected enrollmentHelper: VmaEnrollmentHelper,
  ) {
    super(numberService, currencyService, stringHelper);
  }

  isHomeLoanUpdateEnabled = inject(VMA_HOME_LOAN_ENABLED);

  public categoryTranslationKey(
    category: PointsTransactionCategory | 'all',
  ): string {
    const EXPIRED_TRANSLATION_KEYS = [
      PointsTransactionCategory.WriteOffNegative,
      PointsTransactionCategory.WriteOff,
      PointsTransactionCategory.Expiry,
    ];
    if (!!EXPIRED_TRANSLATION_KEYS.find((x) => category === x)) {
      return 'wl.vma.expired';
    }
    return `app.${category}`;
  }

  cardSpendMetadataTotalSpend(pointsTransaction: PointsTransaction): string {
    const { spendAmount } = pointsTransaction.metadata as CardSpendMetadata;

    if (spendAmount >= 0) {
      return `$${this.numberService.formatNumber(spendAmount)}`;
    } else {
      return `($${this.numberService.formatNumber(Math.abs(spendAmount))})`;
    }
  }

  hotelMetadataCashPaidWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { cashPaid } = pointsTransaction.orderItemData as TravelHotelMetadata;

    return `$${this.numberService.formatNumber(cashPaid, '1.2-2')}`;
  }

  carMetadataCashPaidWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { cashPaid } = pointsTransaction.orderItemData as TravelCarMetadata;

    return `$${this.numberService.formatNumber(cashPaid, '1.2-2')}`;
  }

  flightMetadataCashPaidWithCurrency(
    pointsTransaction: PointsTransaction,
  ): string {
    const { cashPaid } =
      pointsTransaction.orderItemData as TravelFlightMetadata;

    return `$${this.numberService.formatNumber(cashPaid, '1.2-2')}`;
  }

  redeemCashMetadataAmount(pointsTransaction: PointsTransaction): string {
    const { amount } = pointsTransaction.orderItemData as RedeemCashMetadata;

    return `$${this.numberService.formatNumber(amount, '1.2-2')}`;
  }

  redeemCashProductCategory(pointsTransaction: PointsTransaction): string {
    const orderItemData = pointsTransaction.orderItemData as RedeemCashMetadata;

    return this.isHomeLoanUpdateEnabled &&
      this.enrollmentHelper.isHomeLoan(
        orderItemData.membership?.enrollment as Enrollment,
      )
      ? 'homeloan'
      : super.redeemCashProductCategory(pointsTransaction);
  }
}
